/*global google */
import React, { Component } from 'react';

import './BundleMonitorMap.scss';
import {
  middleOfTheWorld,
  OrderClazz,
  OrderStatus,
} from '../../types/orderTypes';
import {
  BundleJson,
  CourierJson,
  InstructionType,
  TravelMode,
} from '../../types/bundleTypes';
import { circle } from '../../components/maps/mapMarkers';
import { MapRouter } from '../../components/maps/MapRouter';
import { zoomMap } from '../../components/maps/mapUtils';
import { getBundleColor } from '../oldPlanner/utils/bundleDraft';
import mapStyles from './BundleMonitorMapStyles';

import BikeIcon from '../../assets/markers/svg/bike.svg';
import CarIcon from '../../assets/markers/svg/car.svg';

export declare interface OrderMarker {
  order: OrderClazz;
  color: string;
  nextDelivery: boolean;
}

class MapMarkerContainer {
  id: string;
  type: InstructionType;
  orderMarker: OrderMarker;
  mapMarker: google.maps.Marker;

  constructor(orderMarker: OrderMarker, type: InstructionType) {
    this.id = MapMarkerContainer.markerKey(orderMarker, type);
    this.type = type;
    this.orderMarker = orderMarker;
  }

  isNextDelivery(): boolean {
    return (
      this.type === InstructionType.Delivery && this.orderMarker.nextDelivery
    );
  }

  getOrder(): OrderClazz {
    return this.orderMarker.order;
  }

  getColor(): string {
    return this.orderMarker.color;
  }

  private static markerKey(
    orderMarker: OrderMarker,
    type: InstructionType
  ): string {
    if (type === InstructionType.Delivery) {
      return `${type}-${orderMarker.order.recipientLatitude}-${orderMarker.order.recipientLongitude}`;
    } else {
      return `${type}-${orderMarker.order.originAddress.location.latitude}-${orderMarker.order.originAddress.location.longitude}`;
    }
  }
}

declare interface Props {
  bundles: BundleJson[];
  orders: OrderMarker[];
  couriers: CourierJson[];
  drawRoutes: boolean;
}

declare interface State {
  isTrafficEnabled: boolean;
}

export default class BundleMonitorMap extends Component<Props, State> {
  private mapContainer: HTMLDivElement;

  private mapInstance: google.maps.Map;
  private infoWindow: google.maps.InfoWindow;
  private trafficLayer: google.maps.TrafficLayer;
  private infoWindowOpen: boolean;
  private mapRouters: Map<string, MapRouter>;

  private markers: MapMarkerContainer[];
  private courierMarkers: google.maps.Marker[];

  constructor(props) {
    super(props);

    this.state = {
      isTrafficEnabled: false,
    };
    this.markers = [];
    this.courierMarkers = [];
    this.mapRouters = new Map();
  }

  componentDidMount() {
    this.infoWindow = new google.maps.InfoWindow();
    this.infoWindowOpen = false;
    this.mapInstance = new google.maps.Map(this.mapContainer, {
      zoom: 10,
      center: middleOfTheWorld,
      clickableIcons: false,
      disableDefaultUI: true,
      mapTypeControl: false,
      zoomControl: true,
      disableDoubleClickZoom: false,
      styles: mapStyles,
    });

    const trafficInfoControl = document.getElementById(
      'bundle-monitor__traffic-info-filter'
    );
    this.mapInstance.controls[google.maps.ControlPosition.TOP_LEFT].push(
      trafficInfoControl
    );
    google.maps.event.addListenerOnce(this.mapInstance, 'tilesloaded', () => {
      // wait until map is ready before showing type filter to avoid flashing effect
      setTimeout(() => {
        trafficInfoControl.style.display = 'grid';
      }, 500);
    });

    this.mapInstance.addListener('click', () => {
      this.infoWindowOpen = false;
      this.infoWindow.close();
    });
  }

  async componentDidUpdate(
    prevProps: Readonly<Props>,
    prevState: Readonly<State>
  ) {
    // Reset filter when changing dates. Can we use more than the length to check? The order ids?
    if (this.props.orders.length !== prevProps.orders.length) {
      this.clearMarkers();
    }
    this.updateMarkers();
    this.addCourierMarkers();

    const bundlesHash = this.props.bundles
      .map(bundle => this.bundleHashString(bundle))
      .sort()
      .join(',');
    const prevBundlesHash = prevProps.bundles
      .map(bundle => this.bundleHashString(bundle))
      .sort()
      .join(',');

    const couriersHash = this.props.couriers
      .map(courier => this.courierHashString(courier))
      .sort()
      .join(',');
    const prevCouriersHash = prevProps.couriers
      .map(courier => this.courierHashString(courier))
      .sort()
      .join(',');

    if (bundlesHash !== prevBundlesHash) {
      this.clearRoutes();
      if (this.props.drawRoutes) {
        this.drawRoutes();
      }
      if (this.props.bundles.length !== prevProps.bundles.length) {
        this.zoomToOrders();
      }
    } else if (couriersHash !== prevCouriersHash) {
      if (this.props.bundles.length !== prevProps.bundles.length) {
        this.zoomToOrders();
      }
    }
  }

  bundleHashString(bundle: BundleJson): string {
    return [bundle.id, bundle.status]
      .concat(
        bundle.instructions.map(instr =>
          [instr.orderId, instr.type, instr.completed].join('-')
        )
      )
      .join(',');
  }

  courierHashString(courier: CourierJson): string {
    return [
      courier.id,
      courier.latitude,
      courier.longitude,
      courier.status,
    ].join('-');
  }

  toggleTrafficLayer() {
    if (this.state.isTrafficEnabled) {
      this.trafficLayer.setMap(null);
      this.setState({ isTrafficEnabled: false });
    } else {
      this.trafficLayer = new google.maps.TrafficLayer();
      this.trafficLayer.setMap(this.mapInstance);
      this.setState({ isTrafficEnabled: true });
    }
  }

  private drawRoutes() {
    const markerLookup = this.markers.reduce(
      (map, multipleMarkersContainer) => {
        map.set(
          multipleMarkersContainer.getOrder().id,
          multipleMarkersContainer
        );
        return map;
      },
      new Map<string, MapMarkerContainer>()
    );

    const bundles = this.props.bundles;

    bundles.forEach(bundle => {
      const bundleRouteMarkers = bundle.instructions
        .map(instr => {
          const marker = markerLookup.get(instr.orderId);
          return marker?.getOrder().location(instr.type);
        })
        .filter(marker => !!marker);

      const mapRouter = new MapRouter({
        map: this.mapInstance,
        strokeColor: getBundleColor(bundle.id),
        strokeOpacity: 0.5,
        strokeWeight: 5,
      });
      this.mapRouters.set(bundle.id, mapRouter);

      mapRouter.drawRoute(bundleRouteMarkers);
    });

    if (bundles.length === 0) {
      setTimeout(() => {
        this.mapInstance.setCenter(middleOfTheWorld);
        this.mapInstance.panTo(middleOfTheWorld);
        this.mapInstance.setZoom(7);
      }, 1000);
    }
  }

  private clearRoutes() {
    Array.from(this.mapRouters.values()).forEach(mapRouter => mapRouter.hide());
    this.mapRouters.clear();
  }

  private zoomToOrders() {
    const allMarkers = this.markers
      .map(marker => marker.mapMarker)
      .concat(this.courierMarkers);
    zoomMap(allMarkers, this.mapInstance);
  }

  clearMarkers = () => {
    this.markers.forEach(container => container.mapMarker.setMap(null));
    this.markers = [];
  };

  updateMarker = (
    existingMarkerContainer: MapMarkerContainer,
    newMarkerContainer: MapMarkerContainer
  ) => {
    const markerOpts = this.createMarkerOptsForContainer(newMarkerContainer);

    existingMarkerContainer.mapMarker.setOptions(markerOpts);
    existingMarkerContainer.orderMarker = newMarkerContainer.orderMarker;
  };

  updateMarkers = () => {
    const orderMarkers = this.props.orders;

    const markerLookup = this.markers.reduce(
      (map, multipleMarkersContainer) => {
        map.set(multipleMarkersContainer.id, multipleMarkersContainer);
        return map;
      },
      new Map<string, MapMarkerContainer>()
    );

    const generatedMarkers: MapMarkerContainer[] = orderMarkers
      .map(orderMarker => {
        return [
          new MapMarkerContainer(orderMarker, InstructionType.Delivery),
          new MapMarkerContainer(orderMarker, InstructionType.Pickup),
        ];
      })
      .flat();

    // Create actual Google markers for each
    generatedMarkers.forEach(markerContainer => {
      markerContainer.mapMarker = this.createMarker(markerContainer);
    });

    const newAndUpdatedMarkers: MapMarkerContainer[] = generatedMarkers
      .filter(
        multipleMarkerContainer =>
          ![OrderStatus.Delivered, OrderStatus.Canceled].includes(
            multipleMarkerContainer.getOrder().status
          )
      )
      .filter(multipleMarkerContainer => {
        return !(
          multipleMarkerContainer.getOrder().isSentFromHub() &&
          multipleMarkerContainer.type === InstructionType.Pickup
        );
      })
      .map(multipleMarkerContainer => {
        const existingContainer = markerLookup.get(multipleMarkerContainer.id);
        if (existingContainer) {
          this.updateMarker(existingContainer, multipleMarkerContainer);
          return existingContainer;
        } else {
          return multipleMarkerContainer;
        }
      });

    newAndUpdatedMarkers.forEach(multipleMarkerContainer => {
      multipleMarkerContainer.mapMarker.setVisible(true);
      multipleMarkerContainer.mapMarker.setMap(this.mapInstance);
    });

    this.markers = newAndUpdatedMarkers;
  };

  createMarkerOptsForContainer(
    mapContainer: MapMarkerContainer
  ): google.maps.MarkerOptions {
    const size = mapContainer.isNextDelivery() ? 2 : 1;
    return this.createMarkerOpts(
      mapContainer.getOrder(),
      mapContainer.type,
      mapContainer.getColor(),
      size
    );
  }

  createMarkerOpts = (
    order: OrderClazz,
    type: InstructionType,
    color: string,
    size: number
  ): google.maps.MarkerOptions => {
    const latitude =
      type === InstructionType.Pickup
        ? order.originAddress.location.latitude
        : order.recipientLatitude;
    const longitude =
      type === InstructionType.Pickup
        ? order.originAddress.location.longitude
        : order.recipientLongitude;

    return {
      position: {
        lat: latitude,
        lng: longitude,
      },
      icon: circle(color, size),
      title: `${order.id}-${type}`,
      opacity: 1,
    };
  };

  createMarker = (markerContainer: MapMarkerContainer): google.maps.Marker => {
    const marker = new google.maps.Marker(
      this.createMarkerOptsForContainer(markerContainer)
    );
    marker.addListener('click', () => {
      this.infoWindow.setContent(
        this.infoWindowContentMultiple(markerContainer)
      );

      if (!this.infoWindowOpen) {
        this.infoWindow.open(this.mapInstance, marker);
        this.infoWindowOpen = true;
      } else {
        this.infoWindowOpen = false;
        this.infoWindow.close();
      }
    });

    return marker;
  };

  courierInfoWindowContent = (
    courier: CourierJson,
    bundle?: BundleJson
  ): string => {
    const fields: { label: string; value: string }[] = [
      {
        label: 'Name',
        value: courier.fullName,
      },
      {
        label: 'Email',
        value: courier.email,
      },
      {
        label: 'Phone',
        value: `${courier.phoneCode} ${courier.phoneNumber}`,
      },
    ];
    const color = getBundleColor(bundle.id);
    return `<div>
          ${fields
            .map(field => {
              return `
                  <div style="display: flex; justify-content: flex-start">
                    <div>${field.label}: &nbsp;</div>
                    <div style="font-weight: bolder">${field.value}</div>
                  </div>`;
            })
            .join('')}
                <div style="display: flex; justify-content: space-between">
                <div>
                  <span>Bundle: &nbsp; <a href="/bundles/${
                    bundle.id
                  }" target="_blank" rel="noopener noreferrer">${
      bundle.id
    }</a></span></div><span style="border-radius: 50%; margin-left: 20px; width: 0; padding-right: 15px; height: 100%; background-color: ${color};">&nbsp;</span>
                </div>
        </div>`;
  };

  addCourierMarkers = () => {
    this.courierMarkers.forEach(marker => marker.setMap(null));
    this.courierMarkers = [];

    const courierBundles = this.props.bundles.reduce((map, bundle) => {
      if (bundle.assignedCourierId) {
        map.set(Number(bundle.assignedCourierId), bundle);
      }
      return map;
    }, new Map<number, BundleJson>());

    this.props.couriers.forEach(courier => {
      if (courier.latitude == null || courier.longitude == null) {
        return;
      }
      const courierIcon =
        courier.travelMode === TravelMode.BIKE ? BikeIcon : CarIcon;
      const courierMarker = new google.maps.Marker({
        position: {
          lat: courier.latitude,
          lng: courier.longitude,
        },
        icon: {
          url: courierIcon,
          scaledSize: new google.maps.Size(40, 40),
        },
        label: '',
        map: this.mapInstance,
        zIndex: 100,
      });

      courierMarker.addListener('click', () => {
        this.infoWindow.setContent(
          this.courierInfoWindowContent(courier, courierBundles.get(courier.id))
        );
        if (!this.infoWindowOpen) {
          this.infoWindow.open(this.mapInstance, courierMarker);
          this.infoWindowOpen = true;
        } else {
          this.infoWindowOpen = false;
          this.infoWindow.close();
        }
      });

      this.courierMarkers.push(courierMarker);
    });
  };

  infoWindowContentMultiple(markerContainer: MapMarkerContainer): string {
    const orderId = markerContainer.orderMarker.order.id;
    const color = markerContainer.getColor();
    return `<div>
                  <div style="display: flex; justify-content: flex-start">
                    <div>Order: &nbsp;</div>
                    <div style="font-weight: bolder"><a href="/orders/${orderId}" target="_blank" rel="noopener noreferrer">${orderId}</a></div>
                    <span style="border-radius: 50%; margin-left: 20px; width: 0; padding-right: 15px; height: 100%; background-color: ${color};">&nbsp;</span>
                  </div>
        </div>`;
  }

  render() {
    return (
      <>
        <div
          id="bundle-monitor__traffic-info-filter"
          className="bundle-monitor__traffic-info"
        >
          <div>
            <label htmlFor="ShowTrafficInfoCheck">
              <input
                name="ShowTrafficInfoCheck"
                id="ShowTrafficInfoCheck"
                type="checkbox"
                checked={this.state.isTrafficEnabled}
                onChange={() => this.toggleTrafficLayer()}
              />
              Show traffic
            </label>
          </div>
        </div>
        <div
          ref={node => {
            this.mapContainer = node;
          }}
          style={{ width: '100%' }}
        />
      </>
    );
  }
}
